/*
 * ZIP-based freight calculator — Phase 2 (2026-08-16).
 *
 * Calls POST /apps/freight-estimate/calculate ONLY on explicit form submit
 * (button click / Enter key). NEVER on input/keyup/keystroke — the endpoint
 * enforces an 8 request / 5 minute per-visitor cap
 * (docs/zip-freight-calculator-phase1-endpoint-2026-08-16.md) and a
 * per-keystroke caller would exhaust it in a few characters.
 *
 * Response contract (quote.status): estimated | unavailable | quote_required.
 * unavailable/quote_required and any network/timeout failure reveal the
 * existing brand-neutral freight-quote gate (alamo-amgo-freight-gate) already
 * rendered hidden inside this widget — never a broken state, never a new
 * quote form.
 */
(() => {
  const WIDGET_SELECTOR = '[data-alamo-zip-freight-calculator]';
  const REQUEST_TIMEOUT_MS = 15000;

  const moneyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const centsToMoney = (cents) => {
    const value = Number(cents);
    return Number.isFinite(value) ? moneyFormat.format(value / 100) : '';
  };

  const transitLabel = (transit) => {
    if (!transit || typeof transit !== 'object') return '';
    const { minimumDays, maximumDays } = transit;
    const min = Number.isFinite(minimumDays) ? minimumDays : null;
    const max = Number.isFinite(maximumDays) ? maximumDays : null;
    if (min === null && max === null) return '';
    if (min !== null && max !== null && min !== max) return `${min}-${max} business days`;
    const only = min !== null ? min : max;
    return `${only} business day${only === 1 ? '' : 's'}`;
  };

  function selectedVariantId(widget) {
    const scopeFormId = widget.dataset.variantScopeForm;
    const scopeForm = scopeFormId ? document.getElementById(scopeFormId) : null;
    const scope = scopeForm || widget.closest('.product-info') || widget.closest('[data-section-id]') || document;
    const checkedOption = scope.querySelector('[data-variant-id]:checked');
    if (checkedOption?.dataset.variantId) return checkedOption.dataset.variantId;
    const idField = scope.querySelector('input[name="id"], select[name="id"]');
    return idField?.value || '';
  }

  function setLoading(widget, loading) {
    const button = widget.querySelector('[data-alamo-zip-freight-submit]');
    const label = widget.querySelector('[data-alamo-zip-freight-submit-label]');
    const spinner = widget.querySelector('[data-alamo-zip-freight-spinner]');
    if (button) button.disabled = loading;
    if (spinner) spinner.hidden = !loading;
    if (label) label.textContent = loading ? 'Calculating…' : 'Calculate freight';
  }

  function clearResults(widget) {
    const results = widget.querySelector('[data-alamo-zip-freight-results]');
    if (!results) return;
    results.innerHTML = '';
    results.hidden = true;
  }

  function hideFallback(widget) {
    const fallback = widget.querySelector('[data-alamo-zip-freight-fallback]');
    if (fallback) fallback.hidden = true;
  }

  function showFallback(widget, note) {
    const fallback = widget.querySelector('[data-alamo-zip-freight-fallback]');
    if (!fallback) return;
    const noteNode = fallback.querySelector('[data-alamo-zip-freight-fallback-note]');
    if (noteNode) noteNode.textContent = note;
    fallback.hidden = false;
    // Reuse the existing gate's own open behavior (alamo-amgo-freight-gate.js)
    // instead of duplicating panel-toggle logic.
    const opener = fallback.querySelector('[data-amgo-freight-open]');
    if (opener) {
      opener.click();
      window.requestAnimationFrame(() => {
        fallback.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }

  function renderQuantityNotice(results, quote) {
    if (!quote.quantityCapped) return;
    const notice = document.createElement('p');
    notice.className = 'alamo-zip-freight-quantity-notice';
    const requested = quote.requestedQuantity;
    const cap = quote.quantityCap;
    notice.textContent = `You requested ${requested}; this item is rated up to ${cap} per line at checkout. Pricing below reflects ${cap}. Contact us for a larger order.`;
    results.appendChild(notice);
  }

  function renderOptionCard(option) {
    const card = document.createElement('div');
    card.className = 'alamo-zip-freight-card';
    if (option.isFreeWarehousePickup) card.classList.add('alamo-zip-freight-card-free');

    const header = document.createElement('div');
    header.className = 'alamo-zip-freight-card-header';

    const name = document.createElement('span');
    name.className = 'alamo-zip-freight-card-name';
    name.textContent = option.serviceName || 'Shipping option';
    header.appendChild(name);

    const price = document.createElement('span');
    price.className = 'alamo-zip-freight-card-price';
    price.textContent = option.isFreeWarehousePickup ? 'Free' : centsToMoney(option.totalAmountCents);
    header.appendChild(price);

    card.appendChild(header);

    if (option.description) {
      const desc = document.createElement('p');
      desc.className = 'alamo-zip-freight-card-description';
      desc.textContent = option.description;
      card.appendChild(desc);
    }

    const transit = transitLabel(option.transit);
    if (transit) {
      const transitNode = document.createElement('p');
      transitNode.className = 'alamo-zip-freight-card-transit';
      transitNode.textContent = `Estimated transit: ${transit}`;
      card.appendChild(transitNode);
    }

    return card;
  }

  function renderEstimated(widget, quote) {
    const results = widget.querySelector('[data-alamo-zip-freight-results]');
    if (!results) return;
    results.innerHTML = '';
    hideFallback(widget);

    renderQuantityNotice(results, quote);

    const grid = document.createElement('div');
    grid.className = 'alamo-zip-freight-grid';
    const candidates = Array.isArray(quote.candidates) ? quote.candidates : [];
    candidates.forEach((option) => grid.appendChild(renderOptionCard(option)));
    results.appendChild(grid);

    const confirmNote = document.createElement('p');
    confirmNote.className = 'alamo-zip-freight-confirm-note';
    confirmNote.textContent = 'Rates are confirmed at checkout using the same pricing shown here.';
    results.appendChild(confirmNote);

    results.hidden = false;
  }

  function handleQuoteResponse(widget, payload) {
    const quote = payload && typeof payload === 'object' ? payload.quote : null;
    if (!quote || typeof quote !== 'object') {
      showFallback(widget, 'We could not get an instant rate for that ZIP code. Request a freight quote below and we will confirm shipping before payment.');
      return;
    }
    if (quote.status === 'estimated') {
      renderEstimated(widget, quote);
      return;
    }
    if (quote.status === 'quote_required') {
      clearResults(widget);
      showFallback(widget, 'This item needs a custom freight quote for your destination. Request one below — usually within one business day.');
      return;
    }
    // 'unavailable' (or any unrecognized status) — arrange delivery by hand,
    // never a fabricated number, never a dead end.
    clearResults(widget);
    showFallback(widget, 'We need to arrange delivery to this ZIP code by hand. Request a freight quote below and we will confirm the total before payment.');
  }

  function handleFailure(widget) {
    clearResults(widget);
    showFallback(widget, 'We could not reach the freight calculator right now. Request a freight quote below and we will confirm shipping before payment.');
  }

  async function submitCalculation(widget) {
    const endpoint = widget.dataset.endpoint;
    const zipField = widget.querySelector('[data-alamo-zip-freight-zip]');
    const qtyField = widget.querySelector('[data-alamo-zip-freight-qty]');
    const variantId = selectedVariantId(widget);

    if (!variantId) {
      handleFailure(widget);
      return;
    }

    const destinationZip = (zipField?.value || '').trim();
    if (!/^\d{5}$/.test(destinationZip)) {
      zipField?.setCustomValidity('Enter a 5-digit ZIP code.');
      zipField?.reportValidity();
      return;
    }
    zipField?.setCustomValidity('');

    const cap = Number.parseInt(widget.dataset.quantityCap || '1', 10) || 1;
    let quantity = Number.parseInt(qtyField?.value || '1', 10);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
    if (quantity > cap) quantity = cap;
    if (qtyField) qtyField.value = quantity;

    setLoading(widget, true);
    clearResults(widget);
    hideFallback(widget);

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = controller ? window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ variantId: String(variantId), destinationZip, quantity }),
        signal: controller ? controller.signal : undefined,
      });

      if (!response.ok) {
        handleFailure(widget);
        return;
      }

      const payload = await response.json();
      handleQuoteResponse(widget, payload);
    } catch (_error) {
      handleFailure(widget);
    } finally {
      if (timeout) window.clearTimeout(timeout);
      setLoading(widget, false);
    }
  }

  function onVariantChanged(widget) {
    // Selected options changed since the last calculation. Clear any shown
    // results rather than silently keeping a price for the wrong variant.
    // Per the no-per-keystroke rule, this does NOT auto-recalculate; the
    // shopper presses Calculate again.
    const results = widget.querySelector('[data-alamo-zip-freight-results]');
    if (results && !results.hidden) {
      clearResults(widget);
      hideFallback(widget);
    }
  }

  function bindWidget(widget) {
    if (widget.dataset.alamoZipFreightBound === 'true') return;
    widget.dataset.alamoZipFreightBound = 'true';

    const form = widget.querySelector('[data-alamo-zip-freight-form]');
    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitCalculation(widget);
      });
    }

    const scopeFormId = widget.dataset.variantScopeForm;
    const scopeForm = scopeFormId ? document.getElementById(scopeFormId) : null;
    const scope = scopeForm || widget.closest('.product-info') || document;
    const onScopeChange = () => onVariantChanged(widget);
    scope.addEventListener('change', onScopeChange);
    document.addEventListener('variant:change', onScopeChange);
    document.addEventListener('product:variant-change', onScopeChange);
  }

  function bindAll() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(bindWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  } else {
    bindAll();
  }
})();
